<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <b-modal
      v-model="networkError"
      centered
      modal-class="modal-danger"
      title="Błąd połączenia"
      ok-only
      ok-variant="light"
      ok-title="OK"
    >
      Nie udało się połączyć z serwerem.<br>Sprawdź połączenie z Internetem i spróbuj ponownie.
    </b-modal>

    <b-modal
      v-model="globalError"
      centered
      modal-class="modal-danger"
      title="Błąd aplikacji"
      ok-only
      ok-variant="light"
      ok-title="OK"
    >{{ globalErrorText }}</b-modal>
    <b-modal
      v-model="serverError"
      centered
      modal-class="modal-danger"
      title="Błąd serwera"
      ok-only
      ok-variant="light"
      ok-title="OK"
    >Niestety, wystąpił błąd serwera aplikacji.<br>Spróbuj ponownie później.</b-modal>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutFull,
  },
  data() {
    return {
      networkError: false,
      serverError: false,
      globalErrorText: null,
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    globalError: {
      get() {
        return !!this.globalErrorText
      },
      set(value) {
        this.globalErrorText = value
      },
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    // handle global logout event
    window.addEventListener('auth:unauthorized', () => {
      this.$store.dispatch('auth/logout')
      if (this.$route.name !== 'login') {
        this.$router.replace({ path: '/login', query: { to: this.$route.fullPath } })
      }
    })

    // handle global network error
    window.addEventListener('network:globalError', () => {
      this.networkError = true
    })
    window.addEventListener('network:serverError', () => {
      this.serverError = true
    })

    this.$root.$on('global:error', (text => {
      this.globalErrorText = text
    }))
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
}
</script>
